// src/features/Planning/components/Step4PlotSizeNonRectangular.jsx
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  VStack,
  HStack,
  InputGroup,
  InputRightAddon,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useEffect, useRef, useState, useCallback } from "react";
import CompassImage from "../../../../assets/Planning/Compass/illustration-compass 1.png";
import { normalizeDirection } from "../../../../utils/api";

// ─── Tiny inline alert ────────────────────────────────────────────────────────

const svgSize = 280;

// Step2Direction.jsx / Step3Peripheries.jsx jaisa hi angle map — taaki
// compass yahan bhi Step 2 mein select ki hui direction ke hisaab se
// sahi rotate ho (pehle ye sirf decorative/static tha, direction ignore karta tha).
const directionAngles = {
  north: 0,
  "north-east": 45,
  east: 90,
  "south-east": 135,
  south: 180,
  "south-west": 225,
  west: 270,
  "north-west": 315,
};


function getCentroid(points) {
  let x = 0, y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

function clampOffset(offset, front, back, left, right, minSeparation) {
  const delta = (front - back) / 2;
  const maxDxLeft = Math.sqrt(left ** 2 - minSeparation ** 2);
  const maxDxRight = Math.sqrt(right ** 2 - minSeparation ** 2);
  const minO = Math.max(-maxDxLeft - delta, -maxDxRight + delta);
  const maxO = Math.min(maxDxLeft - delta, maxDxRight + delta);
  return Math.max(minO, Math.min(maxO, offset));
}

function angleBetween(A, B, C) {
  const v1x = A.x - B.x;
  const v1y = A.y - B.y;
  const v2x = C.x - B.x;
  const v2y = C.y - B.y;

  const dot = v1x * v2x + v1y * v2y;
  const mag1 = Math.hypot(v1x, v1y);
  const mag2 = Math.hypot(v2x, v2y);
  if (!mag1 || !mag2) return 0;

  let cosTheta = dot / (mag1 * mag2);
  cosTheta = Math.max(-1, Math.min(1, cosTheta));
  const rad = Math.acos(cosTheta);
  return (rad * 180) / Math.PI;
}

// Convert feet + inches to decimal feet
function convertToDecimalFeet(feet, inches) {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  return f + (i / 12);
}

export default function Step4PlotSizeNonRectangular({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const [showChangeAlert, setShowChangeAlert] = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const originalSize = useRef(formData.size);

  const hasPrefill = !!(
    parseFloat(originalSize.current?.frontFeet) > 0 ||
    parseFloat(originalSize.current?.backFeet)  > 0 ||
    parseFloat(originalSize.current?.leftFeet)  > 0 ||
    parseFloat(originalSize.current?.rightFeet) > 0
  );

  const triggerAlert = () => {
    if (hasPrefill) setShowChangeAlert(true);
  };

  // Handlers for feet inputs
  const handleFrontFeetChange = (e) => {
    triggerAlert();
    setFormData({ ...formData, size: { ...(formData.size || {}), frontFeet: e.target.value } });
  };
  
  const handleFrontInchesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 0 && Number(value) <= 11)) {
      triggerAlert();
      setFormData({ ...formData, size: { ...(formData.size || {}), frontInches: value } });
    }
  };

  const handleBackFeetChange = (e) => {
    triggerAlert();
    setFormData({ ...formData, size: { ...(formData.size || {}), backFeet: e.target.value } });
  };
  
  const handleBackInchesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 0 && Number(value) <= 11)) {
      triggerAlert();
      setFormData({ ...formData, size: { ...(formData.size || {}), backInches: value } });
    }
  };

  const handleLeftFeetChange = (e) => {
    triggerAlert();
    setFormData({ ...formData, size: { ...(formData.size || {}), leftFeet: e.target.value } });
  };
  
  const handleLeftInchesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 0 && Number(value) <= 11)) {
      triggerAlert();
      setFormData({ ...formData, size: { ...(formData.size || {}), leftInches: value } });
    }
  };

  const handleRightFeetChange = (e) => {
    triggerAlert();
    setFormData({ ...formData, size: { ...(formData.size || {}), rightFeet: e.target.value } });
  };
  
  const handleRightInchesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 0 && Number(value) <= 11)) {
      triggerAlert();
      setFormData({ ...formData, size: { ...(formData.size || {}), rightInches: value } });
    }
  };

  // FIX 2: Reset Shape - offset ko 0 karo taaki back line center pe aa jaye
  const handleResetShape = () => {
    setFormData({
      ...formData,
      size: { ...(formData.size || {}), offset: 0 },
    });
  };

  const svgRef = useRef(null);
  const [points, setPoints] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [initialDragX, setInitialDragX] = useState(null);
  const [currentScale, setCurrentScale] = useState(4);
  const [viewBox, setViewBox] = useState("0 0 400 400");

  // defaults once on mount only
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      size: {
        ...(prev.size || {}),
        frontFeet:   prev.size?.frontFeet   ?? "70",
        frontInches: prev.size?.frontInches ?? "",
        backFeet:    prev.size?.backFeet    ?? "50",
        backInches:  prev.size?.backInches  ?? "",
        leftFeet:    prev.size?.leftFeet    ?? "40",
        leftInches:  prev.size?.leftInches  ?? "",
        rightFeet:   prev.size?.rightFeet   ?? "50",
        rightInches: prev.size?.rightInches ?? "",
        offset:      prev.size?.offset      ?? 0,
      },
    }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Convert feet+inches to decimal feet for calculations
    const front = convertToDecimalFeet(
      formData.size?.frontFeet || "70",
      formData.size?.frontInches || "0"
    );
    const back = convertToDecimalFeet(
      formData.size?.backFeet || "50",
      formData.size?.backInches || "0"
    );
    const left = convertToDecimalFeet(
      formData.size?.leftFeet || "40",
      formData.size?.leftInches || "0"
    );
    const right = convertToDecimalFeet(
      formData.size?.rightFeet || "50",
      formData.size?.rightInches || "0"
    );
    
    let offset = parseFloat(formData.size?.offset) || 0;

    const minSeparation = 1;
    offset = clampOffset(offset, front, back, left, right, minSeparation);

    const bl_x = -front / 2;
    const br_x = front / 2;
    const tl_x = -back / 2 + offset;
    const tr_x = back / 2 + offset;

    const dx_left = tl_x - bl_x;
    const dx_right = tr_x - br_x;

    const h_left = Math.sqrt(Math.max(minSeparation ** 2, left ** 2 - dx_left ** 2));
    const h_right = Math.sqrt(Math.max(minSeparation ** 2, right ** 2 - dx_right ** 2));

    const logical = {
      bl: { x: bl_x, y: 0 },
      br: { x: br_x, y: 0 },
      tl: { x: tl_x, y: h_left },
      tr: { x: tr_x, y: h_right },
    };

    const xs = [logical.bl.x, logical.br.x, logical.tl.x, logical.tr.x];
    const ys = [logical.bl.y, logical.br.y, logical.tl.y, logical.tr.y];

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const spanX = maxX - minX;
    const spanY = maxY - minY;

    const padding = 60;
    const availableWidth = svgSize - padding;
    const availableHeight = svgSize - padding;

    let scale = Math.min(availableWidth / (spanX || 1), availableHeight / (spanY || 1));
    scale = Math.min(scale, 6);

    setCurrentScale(scale);

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const svgCenterX = svgSize / 2;
    const svgCenterY = svgSize / 2;

    setViewBox(`0 0 ${svgSize} ${svgSize}`);
    setPoints({ ...logical, centerX, centerY, svgCenterX, svgCenterY });
  // Use individual primitives — formData.size object reference changes every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.size?.frontFeet, formData.size?.frontInches,
    formData.size?.backFeet,  formData.size?.backInches,
    formData.size?.leftFeet,  formData.size?.leftInches,
    formData.size?.rightFeet, formData.size?.rightInches,
    formData.size?.offset,
  ]);

  const handleMouseDown = (key, e) => {
    e.preventDefault();
    if (!points) return;

    const svg_rect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svg_rect.left;
    const logicalX = (mouseX - points.svgCenterX) / currentScale + points.centerX;

    let initX;
    if (key === "tl") {
      initX = points.tl.x;
    } else if (key === "tr") {
      initX = points.tr.x;
    } else if (key === "backLine") {
      initX = logicalX;
    }

    setInitialDragX(initX);
    setDragging(key);
  };

  const handleMouseUp = useCallback(() => {
    if (dragging && points) {
      const front = convertToDecimalFeet(
        formData.size?.frontFeet,
        formData.size?.frontInches
      );
      const back = convertToDecimalFeet(
        formData.size?.backFeet,
        formData.size?.backInches
      );
      const left = convertToDecimalFeet(
        formData.size?.leftFeet,
        formData.size?.leftInches
      );
      const right = convertToDecimalFeet(
        formData.size?.rightFeet,
        formData.size?.rightInches
      );

      let new_offset;
      if (dragging === "backLine") {
        new_offset = (points.tl.x + points.tr.x) / 2;
      } else {
        new_offset = points.tl.x + back / 2;
      }

      const bl_x = -front / 2;
      const br_x = front / 2;
      const tl_x = -back / 2 + new_offset;
      const tr_x = back / 2 + new_offset;

      const dx_left = tl_x - bl_x;
      const dx_right = tr_x - br_x;

      const h_left_sq = left ** 2 - dx_left ** 2;
      const h_right_sq = right ** 2 - dx_right ** 2;

      const h_left = h_left_sq >= 0 ? Math.sqrt(h_left_sq) : -Math.sqrt(-h_left_sq);
      const h_right = h_right_sq >= 0 ? Math.sqrt(h_right_sq) : -Math.sqrt(-h_right_sq);

      setPoints({
        ...points,
        tl: { x: tl_x, y: h_left },
        tr: { x: tr_x, y: h_right },
      });

      setFormData({
        ...formData,
        size: { ...formData.size, offset: new_offset.toFixed(1) },
      });
    }
    setDragging(null);
    setInitialDragX(null);
  }, [dragging, points, formData, setFormData]);

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging || !points || initialDragX === null) return;

      const svg_rect = svgRef.current.getBoundingClientRect();
      const mouseX = e.clientX - svg_rect.left;
      const logical_mouse_x = (mouseX - points.svgCenterX) / currentScale + points.centerX;

      const front = convertToDecimalFeet(
        formData.size?.frontFeet,
        formData.size?.frontInches
      );
      const back = convertToDecimalFeet(
        formData.size?.backFeet,
        formData.size?.backInches
      );
      const left = convertToDecimalFeet(
        formData.size?.leftFeet,
        formData.size?.leftInches
      );
      const right = convertToDecimalFeet(
        formData.size?.rightFeet,
        formData.size?.rightInches
      );

      const delta = logical_mouse_x - initialDragX;
      const current_offset = parseFloat(formData.size?.offset) || 0;
      let proposed_offset = current_offset + delta;

      const minSeparation = 1;
      const clamped_offset = clampOffset(proposed_offset, front, back, left, right, minSeparation);

      const new_tl_x = clamped_offset - back / 2;
      const new_tr_x = clamped_offset + back / 2;

      const new_dx_left = new_tl_x - points.bl.x;
      const new_dx_right = new_tr_x - points.br.x;

      const new_h_left = Math.sqrt(Math.max(minSeparation ** 2, left ** 2 - new_dx_left ** 2));
      const new_h_right = Math.sqrt(Math.max(minSeparation ** 2, right ** 2 - new_dx_right ** 2));

      setPoints({
        ...points,
        tl: { x: new_tl_x, y: new_h_left },
        tr: { x: new_tr_x, y: new_h_right },
      });
    },
    [dragging, points, initialDragX, currentScale, formData]
  );

  const lineMidpoint = (p1, p2) => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  });

  const lineLength = (p1, p2) => Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2).toFixed(1);

  const transform = (p) => {
    if (!points) return { x: 0, y: 0 };
    return {
      x: (p.x - points.centerX) * currentScale + points.svgCenterX,
      y: (points.centerY - p.y) * currentScale + points.svgCenterY,
    };
  };

  const renderDiagram = () => {
    if (!points) return null;

    const { bl, br, tl, tr } = points;

    const sbl = transform(bl);
    const sbr = transform(br);
    const stl = transform(tl);
    const str = transform(tr);

    const centroid_logical = getCentroid([bl, br, tr, tl]);
    const centroid = transform(centroid_logical);

    // Step 2 mein select ki hui direction ke hisaab se compass rotate karo
    const normalizedDirection = normalizeDirection(formData?.direction);
    const selectedAngle = directionAngles[normalizedDirection] ?? 0;
    const compassRotation = normalizedDirection ? 180 - selectedAngle : 0;

    const frontLabelPos = transform(lineMidpoint(bl, br));
    const backLabelPos = transform(lineMidpoint(tl, tr));
    const leftLabelPos = transform(lineMidpoint(bl, tl));
    const rightLabelPos = transform(lineMidpoint(br, tr));

    let leftRectX = leftLabelPos.x - 38;
    let leftTextX = leftLabelPos.x - 23;
    if (leftRectX < 0) {
      leftRectX = leftLabelPos.x + 8;
      leftTextX = leftLabelPos.x + 23;
    }

    let rightRectX = rightLabelPos.x + 8;
    let rightTextX = rightLabelPos.x + 23;
    if (rightRectX + 30 > svgSize) {
      rightRectX = rightLabelPos.x - 38;
      rightTextX = rightLabelPos.x - 23;
    }

    return (
      <svg
        ref={svgRef}
        width={svgSize}
        height={svgSize}
        viewBox={viewBox}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          border: "1px solid #cbd5e0",
          borderRadius: "10px",
          cursor: dragging ? "grabbing" : "default",
        }}
      >
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
          </pattern>
          <linearGradient id="plotGrad" x1="0%" y1="0%" x2="100%">
            <stop offset="0%" stopColor="rgba(56, 178, 172, 0.2)" />
            <stop offset="100%" stopColor="rgba(14, 116, 144, 0.3)" />
          </linearGradient>
        </defs>

        <rect width="100%" height="100%" fill="url(#grid)" />

        <polygon
          points={`${sbl.x},${sbl.y} ${sbr.x},${sbr.y} ${str.x},${str.y} ${stl.x},${stl.y}`}
          fill="url(#plotGrad)"
          stroke="#2d3748"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        <line x1={sbl.x} y1={sbl.y} x2={sbr.x} y2={sbr.y} stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" />
        <line
          x1={stl.x}
          y1={stl.y}
          x2={str.x}
          y2={str.y}
          stroke="#38a169"
          strokeWidth="5"
          strokeLinecap="round"
          onMouseDown={(e) => handleMouseDown("backLine", e)}
          style={{ cursor: dragging === "backLine" ? "grabbing" : "grab" }}
        />
        <line x1={sbl.x} y1={sbl.y} x2={stl.x} y2={stl.y} stroke="#ed8936" strokeWidth="2.5" strokeLinecap="round" />
        <line x1={sbr.x} y1={sbr.y} x2={str.x} y2={str.y} stroke="#ed8936" strokeWidth="2.5" strokeLinecap="round" />

        <line
          x1={sbl.x}
          y1={sbl.y}
          x2={str.x}
          y2={str.y}
          stroke="#805ad5"
          strokeWidth="1.5"
          strokeDasharray="4,3"
          strokeLinecap="round"
          opacity="0.6"
        />
        <line
          x1={sbr.x}
          y1={sbr.y}
          x2={stl.x}
          y2={stl.y}
          stroke="#805ad5"
          strokeWidth="1.5"
          strokeDasharray="4,3"
          strokeLinecap="round"
          opacity="0.6"
        />

        <g>
          <rect
            x={frontLabelPos.x - 30}
            y={frontLabelPos.y + 6}
            width="60"
            height="14"
            fill="white"
            stroke="#38a169"
            strokeWidth="1"
            rx="7"
            opacity="0.95"
          />
          <text x={frontLabelPos.x} y={frontLabelPos.y + 16} textAnchor="middle" fontSize="10" fontWeight="600" fill="#38a169">
            {formData.size?.frontFeet || 0}'{formData.size?.frontInches || 0}"
          </text>
        </g>

        <g>
          <rect
            x={backLabelPos.x - 30}
            y={backLabelPos.y - 28}
            width="60"
            height="14"
            fill="white"
            stroke="#38a169"
            strokeWidth="1"
            rx="7"
            opacity="0.95"
          />
          <text x={backLabelPos.x} y={backLabelPos.y - 18} textAnchor="middle" fontSize="10" fontWeight="600" fill="#38a169">
            {formData.size?.backFeet || 0}'{formData.size?.backInches || 0}"
          </text>
        </g>

        <g>
          <rect
            x={leftRectX}
            y={leftLabelPos.y - 7}
            width="30"
            height="14"
            fill="white"
            stroke="#ed8936"
            strokeWidth="1"
            rx="7"
            opacity="0.95"
          />
          <text x={leftTextX} y={leftLabelPos.y + 3} textAnchor="middle" fontSize="10" fontWeight="600" fill="#ed8936">
            {formData.size?.leftFeet || 0}'{formData.size?.leftInches || 0}"
          </text>
        </g>

        <g>
          <rect
            x={rightRectX}
            y={rightLabelPos.y - 7}
            width="30"
            height="14"
            fill="white"
            stroke="#ed8936"
            strokeWidth="1"
            rx="7"
            opacity="0.95"
          />
          <text x={rightTextX} y={rightLabelPos.y + 3} textAnchor="middle" fontSize="10" fontWeight="600" fill="#ed8936">
            {formData.size?.rightFeet || 0}'{formData.size?.rightInches || 0}"
          </text>
        </g>

        <g
          transform={`rotate(${compassRotation} ${centroid.x} ${centroid.y})`}
          style={{ transition: "transform 0.4s ease-in-out" }}
        >
          <image
            x={centroid.x - 60}
            y={centroid.y - 60}
            width="120"
            height="120"
            href={CompassImage}
            style={{ pointerEvents: "none", opacity: 0.6 }}
          />
        </g>

        <g>
          <circle
            cx={stl.x}
            cy={stl.y}
            r="8"
            fill="#e53e3e"
            stroke="white"
            strokeWidth="2"
            onMouseDown={(e) => handleMouseDown("tl", e)}
            style={{ cursor: dragging === "tl" ? "grabbing" : "grab" }}
          />
          <circle
            cx={str.x}
            cy={str.y}
            r="8"
            fill="#e53e3e"
            stroke="white"
            strokeWidth="2"
            onMouseDown={(e) => handleMouseDown("tr", e)}
            style={{ cursor: dragging === "tr" ? "grabbing" : "grab" }}
          />

          <circle cx={sbl.x} cy={sbl.y} r="6" fill="#718096" stroke="white" strokeWidth="2" />
          <circle cx={sbr.x} cy={sbr.y} r="6" fill="#718096" stroke="white" strokeWidth="2" />
        </g>
      </svg>
    );
  };

  // Validation: Check if all feet fields have values
  const isFormValid = () => {
    const frontFeet = formData.size?.frontFeet;
    const backFeet = formData.size?.backFeet;
    const leftFeet = formData.size?.leftFeet;
    const rightFeet = formData.size?.rightFeet;
    
    return frontFeet && backFeet && leftFeet && rightFeet &&
           parseFloat(frontFeet) > 0 && parseFloat(backFeet) > 0 &&
           parseFloat(leftFeet) > 0 && parseFloat(rightFeet) > 0;
  };

  const handleAction = () => {
    if (!viewMode && !isFormValid()) return;
    // Agar prefilled value change hui hai to confirm dialog dikhao
    if (showChangeAlert) {
      setShowConfirm(true);
      return;
    }
    doNext();
  };

  const doNext = () => {

    // points state se lo — agar null hai to direct formula se calculate karo
    const activePoints = points || (() => {
      const front = convertToDecimalFeet(formData.size?.frontFeet, formData.size?.frontInches);
      const back  = convertToDecimalFeet(formData.size?.backFeet,  formData.size?.backInches);
      const left  = convertToDecimalFeet(formData.size?.leftFeet,  formData.size?.leftInches);
      const right = convertToDecimalFeet(formData.size?.rightFeet, formData.size?.rightInches);
      const offset = parseFloat(formData.size?.offset) || 0;
      const minSep = 1;
      const clampedOffset = clampOffset(offset, front, back, left, right, minSep);
      const bl_x = -front / 2;
      const br_x =  front / 2;
      const tl_x = -back  / 2 + clampedOffset;
      const tr_x =  back  / 2 + clampedOffset;
      const dx_l = tl_x - bl_x;
      const dx_r = tr_x - br_x;
      const h_l = Math.sqrt(Math.max(minSep ** 2, left  ** 2 - dx_l ** 2));
      const h_r = Math.sqrt(Math.max(minSep ** 2, right ** 2 - dx_r ** 2));
      return {
        bl: { x: bl_x, y: 0   },
        br: { x: br_x, y: 0   },
        tl: { x: tl_x, y: h_l },
        tr: { x: tr_x, y: h_r },
      };
    })();

    const { bl, br, tl, tr } = activePoints;
    const d1  = parseFloat(lineLength(bl, tr));
    const d2  = parseFloat(lineLength(br, tl));
    const abl = angleBetween(tl, bl, br);
    const abr = angleBetween(bl, br, tr);
    const atl = angleBetween(bl, tl, tr);
    const atr = angleBetween(tl, tr, br);

    const updatedFormData = {
      ...formData,
      plotDiagonal1: String(d1),
      plotDiagonal2: String(d2),
      cornerAngles: {
        BL: abl.toFixed(1),
        BR: abr.toFixed(1),
        TL: atl.toFixed(1),
        TR: atr.toFixed(1),
      },
    };
    setFormData(updatedFormData);

    if (isLastStep) onSubmit(updatedFormData);
    else onNext(updatedFormData);
  };

  const handleConfirmYes = () => {
    setShowConfirm(false);
    doNext();
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
  };

  // All 4 corner angles
  let angleBL = 0;
  let angleBR = 0;
  let angleTL = 0;
  let angleTR = 0;
  if (points) {
    const { bl, br, tl, tr } = points;
    angleBL = angleBetween(tl, bl, br);
    angleBR = angleBetween(bl, br, tr);
    angleTL = angleBetween(bl, tl, tr);
    angleTR = angleBetween(tl, tr, br);
  }

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#a0aec0" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}
      >
        <VStack spacing={4} align="stretch">
          <Flex align="center" justify="space-between">
            <Text fontWeight="600" color="gray.700" fontSize="17px" letterSpacing="-0.01em">
              Plot Dimensions
            </Text>
            {showChangeAlert && (
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4px"
                border="1px solid #f59e0b"
                borderRadius="4px"
                px="7px"
                py="2px"
                style={{ background: "#fffbeb" }}
              >
                <Text fontSize="10px" color="#b45309" fontWeight="600">
                  ⚠ Editing pre-filled value
                </Text>
              </Box>
            )}
          </Flex>

          <Text fontSize="13px" color="gray.600" textAlign="center" mb={1}>
            Enter dimensions below or drag the top corners to adjust shape
          </Text>

          {/* Inputs with Feet + Inches */}
          <Box bg="white" p={4} borderRadius="12px" border="1px solid" borderColor="gray.200">
            <VStack spacing={3}>
              {/* Front Width */}
              <Box width="100%">
                <Text fontSize="12px" mb={1.5} color="gray.600" fontWeight="500">
                  Front Width <Text as="span" color="red.500">*</Text>
                </Text>
                <HStack spacing={2}>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.frontFeet ?? ""}
                      onChange={handleFrontFeetChange}
                      type="number"
                      min="0"
                      placeholder="0"
                      bg="white"
                      borderColor="green.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px #38a169" }}
                    />
                    <InputRightAddon bg="green.100" color="green.700" fontSize="11px">
                      ft
                    </InputRightAddon>
                  </InputGroup>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.frontInches ?? ""}
                      onChange={handleFrontInchesChange}
                      type="number"
                      min="0"
                      max="11"
                      placeholder=""
                      bg="white"
                      borderColor="green.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px #38a169" }}
                    />
                    <InputRightAddon bg="green.100" color="green.700" fontSize="11px">
                      in
                    </InputRightAddon>
                  </InputGroup>
                </HStack>
              </Box>

              {/* Back Width */}
              <Box width="100%">
                <Text fontSize="12px" mb={1.5} color="gray.600" fontWeight="500">
                  Back Width <Text as="span" color="red.500">*</Text>
                </Text>
                <HStack spacing={2}>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.backFeet ?? ""}
                      onChange={handleBackFeetChange}
                      type="number"
                      min="0"
                      placeholder="0"
                      bg="white"
                      borderColor="green.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px #38a169" }}
                    />
                    <InputRightAddon bg="green.100" color="green.700" fontSize="11px">
                      ft
                    </InputRightAddon>
                  </InputGroup>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.backInches ?? ""}
                      onChange={handleBackInchesChange}
                      type="number"
                      min="0"
                      max="11"
                      placeholder=""
                      bg="white"
                      borderColor="green.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px #38a169" }}
                    />
                    <InputRightAddon bg="green.100" color="green.700" fontSize="11px">
                      in
                    </InputRightAddon>
                  </InputGroup>
                </HStack>
              </Box>

              {/* Left Depth */}
              <Box width="100%">
                <Text fontSize="12px" mb={1.5} color="gray.600" fontWeight="500">
                Left Side Length <Text as="span" color="red.500">*</Text>
                </Text>
                <HStack spacing={2}>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.leftFeet ?? ""}
                      onChange={handleLeftFeetChange}
                      type="number"
                      min="0"
                      placeholder="0"
                      bg="white"
                      borderColor="orange.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "orange.500", boxShadow: "0 0 0 1px #ed8936" }}
                    />
                    <InputRightAddon bg="orange.100" color="orange.700" fontSize="11px">
                      ft
                    </InputRightAddon>
                  </InputGroup>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.leftInches ?? ""}
                      onChange={handleLeftInchesChange}
                      type="number"
                      min="0"
                      max="11"
                      placeholder=""
                      bg="white"
                      borderColor="orange.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "orange.500", boxShadow: "0 0 0 1px #ed8936" }}
                    />
                    <InputRightAddon bg="orange.100" color="orange.700" fontSize="11px">
                      in
                    </InputRightAddon>
                  </InputGroup>
                </HStack>
              </Box>

              {/* Right Depth */}
              <Box width="100%">
                <Text fontSize="12px" mb={1.5} color="gray.600" fontWeight="500">
                Right Side Length <Text as="span" color="red.500">*</Text>
                </Text>
                <HStack spacing={2}>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.rightFeet ?? ""}
                      onChange={handleRightFeetChange}
                      type="number"
                      min="0"
                      placeholder="0"
                      bg="white"
                      borderColor="orange.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "orange.500", boxShadow: "0 0 0 1px #ed8936" }}
                    />
                    <InputRightAddon bg="orange.100" color="orange.700" fontSize="11px">
                      ft
                    </InputRightAddon>
                  </InputGroup>
                  <InputGroup size="sm" flex="1">
                    <Input
                      value={formData.size?.rightInches ?? ""}
                      onChange={handleRightInchesChange}
                      type="number"
                      min="0"
                      max="11"
                      placeholder=""
                      bg="white"
                      borderColor="orange.300"
                      fontSize="13px"
                      textAlign="center"
                      _focus={{ borderColor: "orange.500", boxShadow: "0 0 0 1px #ed8936" }}
                    />
                    <InputRightAddon bg="orange.100" color="orange.700" fontSize="11px">
                      in
                    </InputRightAddon>
                  </InputGroup>
                </HStack>
              </Box>
            </VStack>
          </Box>

          {/* Diagram */}
          <Box width="100%" display="flex" justifyContent="center" bg="white" borderRadius="10px" p={2}>
            {renderDiagram()}
          </Box>

          {/* FIX 2: Reset Shape button - offset 0 karo */}
          <Flex justify="center">
            <Button
              onClick={handleResetShape}
              size="sm"
              variant="outline"
              colorScheme="blue"
            >
              Reset Shape
            </Button>
          </Flex>

          {/* Diagonals + Angles */}
          {points && (
            <>
              <Box bg="purple.50" p={3} borderRadius="8px" border="1px solid" borderColor="purple.200">
                <Text fontSize="11px" fontWeight="600" color="purple.700" mb={1.5}>
                  Diagonal Measurements:
                </Text>
                <HStack spacing={3} fontSize="11px" color="purple.700">
                  <Text>
                    BL → TR: <strong>{lineLength(points.bl, points.tr)} ft</strong>
                  </Text>
                  <Text>
                    BR → TL: <strong>{lineLength(points.br, points.tl)} ft</strong>
                  </Text>
                </HStack>
              </Box>

              <Box bg="blue.50" p={3} borderRadius="8px" border="1px solid" borderColor="blue.200">
                <Text fontSize="11px" fontWeight="600" color="blue.700" mb={1.5}>
                  Corner Angles (approx):
                </Text>
                <SimpleGrid columns={2} spacing={2} fontSize="11px" color="blue.800">
                  <Box>
                    <Text fontWeight="600">Bottom-Left corner</Text>
                    <Text>
                      Between Left & Front: <strong>{angleBL.toFixed(1)}°</strong>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="600">Bottom-Right corner</Text>
                    <Text>
                      Between Front & Right: <strong>{angleBR.toFixed(1)}°</strong>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="600">Top-Left corner</Text>
                    <Text>
                      Between Left & Back: <strong>{angleTL.toFixed(1)}°</strong>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="600">Top-Right corner</Text>
                    <Text>
                      Between Back & Right: <strong>{angleTR.toFixed(1)}°</strong>
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </>
          )}
        </VStack>
      </Box>

      {/* Confirm Dialog */}
      <Modal isOpen={showConfirm} onClose={handleConfirmNo} isCentered size="xs">
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent borderRadius="12px" mx={4}>
          <ModalBody pt={6} pb={2} textAlign="center">
            <Text fontSize="22px" mb={2}>⚠️</Text>
            <Text fontWeight="700" fontSize="15px" color="gray.800" mb={1}>
              Change Pre-filled Value?
            </Text>
            <Text fontSize="13px" color="gray.500">
              You are editing a value that was pre-filled from the database. Are you sure you want to continue?
            </Text>
          </ModalBody>
          <ModalFooter gap={3} justifyContent="center" pb={5}>
            <Button
              onClick={handleConfirmNo}
              variant="outline"
              colorScheme="gray"
              borderRadius="8px"
              size="sm"
              minW="90px"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmYes}
              colorScheme="cyan"
              borderRadius="8px"
              size="sm"
              minW="90px"
            >
              Yes, Continue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Navigation */}
      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{
            bg: "cyan.50",
            borderColor: "cyan.600",
          }}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleAction}
          isDisabled={viewMode ? false : !isFormValid()}
          variant="solid"
          bg="cyan.500"
          color="white"
          border="none"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{
            bg: "cyan.600",
          }}
          _disabled={{
            bg: "gray.300",
            color: "gray.500",
            cursor: "not-allowed",
          }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}